import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import TeamSelector from '../components/TeamSelector';
import {
  UserGroupIcon,
  ArrowDownTrayIcon,
  TableCellsIcon,
  EyeIcon,
  PrinterIcon,
} from '@heroicons/react/24/outline';

const Uniforms = () => {
  const { user, isCoach } = useAuth();
  
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [showAllTeams, setShowAllTeams] = useState(false);
  const [uniforms, setUniforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  // Fetch uniform data from roster
  useEffect(() => {
    const fetchUniformData = async () => {
      if (!selectedTeam) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        const response = await fetch(`/api/roster?teamId=${selectedTeam.id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          const rosterData = await response.json();
          
          // Filter for players only and extract uniform information
          const uniformData = rosterData
            .filter(member => member.role === 'player')
            .map(member => ({
              id: member.id,
              name: member.name,
              email: member.email,
              position: member.position,
              jerseyNumber: member.jerseyNumber,
              sizingType: member.uniform_info?.sizing_type || '',
              hatSize: member.uniform_info?.hat_size || '',
              shirtSize: member.uniform_info?.shirt_size || '',
              pantSize: member.uniform_info?.pant_size || ''
            }));
          
          setUniforms(uniformData);
        }

      } catch (error) {
        console.error('Error fetching uniform data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUniformData();
  }, [selectedTeam]);

  const handleTeamChange = (team) => {
    setSelectedTeam(team);
    setShowAllTeams(false);
  };

  const handleShowAllTeams = () => {
    setShowAllTeams(true);
    setSelectedTeam(null);
  };

  const handleExportCSV = async () => {
    try {
      setExporting(true);
      
      // Generate CSV from current uniform data
      const csvHeaders = ['Player', 'Email', 'Position', 'Jersey #', 'Sizing Type', 'Hat Size', 'Shirt Size', 'Pant Size'];
      
      const csvRows = uniforms.map(player => [
        `"${player.name || ''}"`,
        `"${player.email || ''}"`,
        `"${player.position || ''}"`,
        `"${player.jerseyNumber || ''}"`,
        `"${player.sizingType || ''}"`,
        `"${player.hatSize || ''}"`,
        `"${player.shirtSize || ''}"`,
        `"${player.pantSize || ''}"`
      ].join(','));
      
      const csvContent = [csvHeaders.join(','), ...csvRows].join('\n');
      
      // Create and download the CSV file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedTeam.name}_uniforms_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error('Failed to export CSV. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // No team selected state
  if (!selectedTeam && !showAllTeams) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Uniform Management</h1>
          <TeamSelector 
            onTeamSelect={handleTeamChange}
            onShowAllTeams={handleShowAllTeams}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <UserGroupIcon className="h-8 w-8 text-blue-600 mr-3" />
              Uniform Management
            </h1>
            <p className="text-gray-600 mt-1">
              {selectedTeam ? selectedTeam.name : 'All Teams'} • {uniforms.length} players
            </p>
          </div>
          {isCoach && (
            <div className="flex space-x-3">
              <button
                onClick={handleExportCSV}
                disabled={exporting}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center disabled:opacity-50"
              >
                <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                {exporting ? 'Exporting...' : 'Export CSV'}
              </button>
              <button
                onClick={handlePrint}
                className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 flex items-center"
              >
                <PrinterIcon className="h-5 w-5 mr-2" />
                Print
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Uniform Data Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900 flex items-center">
            <TableCellsIcon className="h-5 w-5 mr-2" />
            Player Uniform Information
          </h2>
        </div>
        
        {uniforms.length === 0 ? (
          <div className="px-6 py-8 text-center text-gray-500">
            No uniform information available for this team.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Player
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Position
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Jersey #
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sizing Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Hat Size
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Shirt Size
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pant Size
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {uniforms.map((player) => (
                  <tr key={player.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {player.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {player.email}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {player.position || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {player.jerseyNumber ? `#${player.jerseyNumber}` : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {player.sizingType || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {player.hatSize || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {player.shirtSize || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {player.pantSize || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Print Styles */}
      <style jsx>{`
        @media print {
          .no-print {
            display: none !important;
          }
          
          .print-header {
            display: block !important;
            text-align: center;
            margin-bottom: 20px;
            font-size: 18px;
            font-weight: bold;
          }
          
          table {
            width: 100% !important;
            border-collapse: collapse !important;
          }
          
          th, td {
            border: 1px solid #000 !important;
            padding: 8px !important;
            text-align: left !important;
          }
          
          th {
            background-color: #f3f4f6 !important;
            font-weight: bold !important;
          }
        }
      `}</style>
    </div>
  );
};

export default Uniforms;
